import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';

import { type Partner } from '@/api/routes/partners/partners.api';
import { IconLock } from '@/components/icon/tabler/IconLock';
import { formatWebsite, isDocumentLocked, maskTaxId } from '@/domain/account/utils';
import { partnerCategoryLabel } from '@/domain/onboarding/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, radiusTokens } from '@/theme/themePrimitives';
import { format, getTaxIdLabel } from '@/utils/data';

interface Props {
  partner: Partner;
}

/** P15 · the profile users see next to the events, and the data the team checked. */
const AccountProfileCard = ({ partner }: Props) => {
  const t = useTranslate('domain.account.components.AccountProfileCard');
  const locked = isDocumentLocked(partner.status);
  const lockNote = locked ? (
    <Box component="span" sx={sx.lock}>
      <IconLock sx={{ fontSize: 14 }} />
      {t('locked')}
    </Box>
  ) : null;
  const region = [partner.city, partner.state].filter(Boolean).join(', ');

  const rows: { key: string; label: string; value: ReactNode }[] = [
    {
      key: 'legalName',
      label: t('legalName'),
      value: (
        <>
          {partner.legalName}
          {lockNote}
        </>
      ),
    },
    {
      key: 'taxId',
      label: getTaxIdLabel(partner.taxId),
      value: (
        <>
          {maskTaxId(partner.taxId)}
          {lockNote}
        </>
      ),
    },
    { key: 'contactName', label: t('contactName'), value: partner.contactName },
    { key: 'contactEmail', label: t('contactEmail'), value: partner.contactEmail },
    {
      key: 'contactPhone',
      label: t('contactPhone'),
      value: partner.contactPhone ? format.phoneNumber(partner.contactPhone) : t('notInformed'),
    },
    {
      key: 'website',
      label: t('website'),
      value: partner.website ? (
        <Link href={partner.website} target="_blank" rel="noreferrer">
          {formatWebsite(partner.website)}
        </Link>
      ) : (
        t('notInformed')
      ),
    },
    {
      key: 'instagram',
      label: t('instagram'),
      value: partner.instagram ? (
        <Link href={`https://instagram.com/${partner.instagram}`} target="_blank" rel="noreferrer">
          @{partner.instagram}
        </Link>
      ) : (
        t('notInformed')
      ),
    },
  ];

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack direction="row" alignItems="center" gap={2.25}>
        <Avatar src={partner.logoUrl ?? undefined} variant="rounded" sx={sx.logo}>
          {partner.displayName.slice(0, 2).toUpperCase()}
        </Avatar>
        <Stack gap={0.5} minWidth={0}>
          <Typography component="h2" variant="h3" sx={sx.name}>
            {partner.displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[partnerCategoryLabel[partner.category], region].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      </Stack>
      <Typography variant="body2" color={partner.description ? 'text.primary' : 'text.secondary'}>
        {partner.description || t('noDescription')}
      </Typography>
      <Divider />
      <Box component="dl" sx={sx.list}>
        {rows.map((row) => (
          <Box key={row.key} sx={sx.row}>
            <Typography component="dt" variant="body2" color="text.secondary" fontWeight={500}>
              {row.label}
            </Typography>
            <Typography component="dd" variant="body2" sx={sx.value}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default AccountProfileCard;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: { xs: 2.5, md: 3 },
    display: 'flex',
    flexDirection: 'column',
    gap: 2.75,
  },
  logo: {
    width: { xs: 64, md: 88 },
    height: { xs: 64, md: 88 },
    borderRadius: { xs: '16px', md: '22px' },
    fontSize: { xs: 20, md: 26 },
    fontWeight: 800,
    bgcolor: controlTokens.primary,
    color: 'common.white',
    flexShrink: 0,
  },
  name: { overflowWrap: 'anywhere' },
  list: { m: 0, display: 'flex', flexDirection: 'column', gap: 1.25 },
  row: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '150px minmax(0, 1fr)' },
    gap: { xs: 0.25, sm: 2 },
    alignItems: 'baseline',
  },
  value: {
    m: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 1.25,
    overflowWrap: 'anywhere',
  },
  lock: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    typography: 'caption',
    color: 'text.secondary',
  },
};
